import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormControl, InputLabel, MenuItem, Select, CircularProgress } from "@mui/material";
import { getDepartmentOptions, DEPARTMENTS } from "utils/departmentUtils";
import { getManageableDepartments } from "utils/ticketAccessUtils";

/**
 * Department select dropdown component
 * Provides a dropdown for selecting a department
 */
function DepartmentSelect({
  value,
  onChange,
  label = "Department",
  id = "department-select",
  name = "department",
  error = false,
  required = false,
  disabled = false,
  fullWidth = true,
  includeEmpty = true,
  emptyLabel = "Select department",
  size = "medium",
  filterByAccess = false, // Whether to filter options by user access
  allowedValues = null, // Optional allow-list of department values
}) {
  const [loading, setLoading] = useState(filterByAccess);
  const [options, setOptions] = useState(() => {
    const base = getDepartmentOptions({ includeEmpty, emptyLabel });
    if (!Array.isArray(allowedValues) || allowedValues.length === 0) {
      return base;
    }
    const allowed = new Set(allowedValues);
    return base.filter((option) => !option.value || allowed.has(option.value));
  });

  // If filterByAccess is true, fetch manageable departments
  useEffect(() => {
    if (filterByAccess) {
      const fetchAccessibleDepartments = async () => {
        setLoading(true);
        try {
          // Get departments the user can manage
          const accessibleDepts = await getManageableDepartments();

          // Create filtered options with only accessible departments
          let filteredDepts = DEPARTMENTS.filter((dept) => accessibleDepts.includes(dept.value));
          if (Array.isArray(allowedValues) && allowedValues.length > 0) {
            const allowed = new Set(allowedValues);
            filteredDepts = filteredDepts.filter((dept) => allowed.has(dept.value));
          }

          // Setup options with empty option if needed
          const newOptions = includeEmpty
            ? [{ label: emptyLabel, value: "" }, ...filteredDepts]
            : filteredDepts;

          setOptions(newOptions);
        } catch (error) {
          console.error("Error filtering departments by access:", error);
          // Fallback to all departments (still respecting allow-list)
          let fallback = getDepartmentOptions({ includeEmpty, emptyLabel });
          if (Array.isArray(allowedValues) && allowedValues.length > 0) {
            const allowed = new Set(allowedValues);
            fallback = fallback.filter((option) => !option.value || allowed.has(option.value));
          }
          setOptions(fallback);
        } finally {
          setLoading(false);
        }
      };

      fetchAccessibleDepartments();
      return;
    }

    let nextOptions = getDepartmentOptions({ includeEmpty, emptyLabel });
    if (Array.isArray(allowedValues) && allowedValues.length > 0) {
      const allowed = new Set(allowedValues);
      nextOptions = nextOptions.filter((option) => !option.value || allowed.has(option.value));
    }
    setOptions(nextOptions);
    setLoading(false);
  }, [filterByAccess, includeEmpty, emptyLabel, allowedValues]);

  return (
    <FormControl
      fullWidth={fullWidth}
      error={error}
      required={required}
      disabled={disabled || loading}
      size={size}
    >
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        name={name}
        value={value}
        label={label}
        onChange={onChange}
        startAdornment={
          loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null
        }
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

DepartmentSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  error: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  includeEmpty: PropTypes.bool,
  emptyLabel: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  filterByAccess: PropTypes.bool,
  allowedValues: PropTypes.arrayOf(PropTypes.string),
};

export default DepartmentSelect;
